import PhoneInput from './common/PhoneInput';
import { useState, useRef } from "react";
import { requestOtp, verifyOtp } from "../services/authService";
import { setAuthData } from "../lib/tokenStore";

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
const OTP_LENGTH = 6;

export default function OtpLogin({ onLoginSuccess }) {
  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const otpRefs = useRef([]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(null);

    const normalized = phoneNumber.startsWith("+")
      ? phoneNumber.replace(/\s/g, "")
      : `+1${phoneNumber.replace(/\s/g, "")}`;

    if (normalized.length < 10) {
      setError("Please enter a valid phone number with country code.");
      return;
    }

    try {
      setLoading(true);
      await requestOtp(normalized);
      setPhoneNumber(normalized);
      setStep("otp");
    } catch (err) {
      setError(err.response?.data?.message || "Unable to send OTP. Please check your number and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-focus next box
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (pasted) {
      const newOtp = [...otp];
      pasted.split("").forEach((char, i) => { newOtp[i] = char; });
      setOtp(newOtp);
      const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
      otpRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);

    const otpValue = otp.join("");
    if (otpValue.length < OTP_LENGTH) {
      setError("Please enter the complete OTP code.");
      return;
    }

    try {
      setLoading(true);
      const response = await verifyOtp(phoneNumber, otpValue);
      setAuthData(response.data);
      onLoginSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid or expired OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
      fontFamily: FONT,
    }}>
      <div style={{
        width: "100%",
        maxWidth: 420,
        background: "#ffffff",
        borderRadius: 20,
        padding: "48px 36px",
        boxShadow: "0 8px 40px rgba(0,0,0,0.08)",
      }}>
        {step === "phone" && (
          <form onSubmit={handleSendOtp}>
            {/* Icon */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16, background: "#111",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M6.5 6.5H17.5V17.5H6.5V6.5Z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M3 12H6.5M17.5 12H21M12 3V6.5M12 17.5V21" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            <h1 style={{
              fontSize: 26, fontWeight: 700, color: "#111",
              textAlign: "center", marginBottom: 8, letterSpacing: "-0.5px",
            }}>
              Welcome to Workout
            </h1>
            <p style={{
              fontSize: 14, color: "#888", textAlign: "center",
              marginBottom: 36, lineHeight: 1.5,
            }}>
              Enter your registered mobile number to get started
            </p>

            <label htmlFor="phone-input" style={{
              fontSize: 13, fontWeight: 600, color: "#111", marginBottom: 8, display: "block",
            }}>
              Phone Number
            </label>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              border: "1.5px solid #e0e0e0", borderRadius: 12, padding: "14px 16px",
              background: "#fafafa", transition: "border-color 0.2s",
            }}>
              <PhoneInput aria-label="Phone number"
                id="phone-input"
                type="tel"
                placeholder="Enter your phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={loading}
                style={{
                  flex: 1, border: "none", outline: "none", fontSize: 15,
                  background: "transparent", color: "#111",
                }}
              />
            </div>

            {error && (
              <p style={{ fontSize: 13, color: "#e53e3e", marginTop: 12, textAlign: "center" }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", padding: "16px", marginTop: 28,
                fontSize: 15, fontWeight: 600, color: "#fff",
                background: "#111", border: "none", borderRadius: 12,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1, transition: "opacity 0.2s",
              }}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={handleVerifyOtp}>
            {/* Icon */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16, background: "#111",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="6" width="18" height="12" rx="2" stroke="#fff" strokeWidth="1.5"/>
                  <path d="M7 12H7.01M12 12H12.01M17 12H17.01" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            <h1 style={{
              fontSize: 26, fontWeight: 700, color: "#111",
              textAlign: "center", marginBottom: 8, letterSpacing: "-0.5px",
            }}>
              Verify OTP
            </h1>
            <p style={{
              fontSize: 14, color: "#888", textAlign: "center",
              marginBottom: 36, lineHeight: 1.5,
            }}>
              We sent a 6-digit code to<br />
              <span style={{ color: "#111", fontWeight: 600 }}>{phoneNumber}</span>
            </p>

            {/* OTP Boxes */}
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 28 }}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={index === 0 ? handleOtpPaste : undefined}
                  autoFocus={index === 0}
                  style={{
                    width: 48, height: 56, textAlign: "center",
                    fontSize: 22, fontWeight: 700, color: "#111",
                    border: digit ? "2px solid #111" : "1.5px solid #e0e0e0",
                    borderRadius: 12, outline: "none",
                    background: digit ? "#f8f8f8" : "#fafafa",
                    transition: "border-color 0.2s, background 0.2s",
                  }}
                />
              ))}
            </div>

            {error && (
              <p style={{ fontSize: 13, color: "#e53e3e", marginBottom: 16, textAlign: "center" }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", padding: "16px",
                fontSize: 15, fontWeight: 600, color: "#fff",
                background: "#111", border: "none", borderRadius: 12,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1, transition: "opacity 0.2s",
              }}
            >
              {loading ? "Verifying..." : "Verify & Login"}
            </button>

            {/* Resend / Change number */}
            <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 20 }}>
              <button
                type="button"
                onClick={() => { setStep("phone"); setOtp(Array(OTP_LENGTH).fill("")); setError(null); }}
                style={{
                  fontSize: 13, color: "#888", background: "none",
                  border: "none", cursor: "pointer", textDecoration: "underline",
                }}
              >
                Change number
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    setError(null);
                    await requestOtp(phoneNumber);
                    setOtp(Array(OTP_LENGTH).fill(""));
                    otpRefs.current[0]?.focus();
                  } catch (err) {
                    setError("Failed to resend OTP.");
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                style={{
                  fontSize: 13, color: "#111", fontWeight: 600,
                  background: "none", border: "none", cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Resend OTP
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
